import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import {
  ArrowLeftIcon,
  CurrencyDollarIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  DocumentDuplicateIcon,
  ArrowUpTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  GlobeAltIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

const RateCards = ({ showNotification }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rateCards, setRateCards] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEntriesModal, setShowEntriesModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);

  // Form state for rate card creation/editing
  const [cardForm, setCardForm] = useState({
    name: '',
    description: '',
    cardType: 'sell',
    currency: 'USD',
    billingIncrement: 6,
    minimumDuration: 6,
    roundingMethod: 'up',
    defaultRate: 0,
    connectionFee: 0
  });

  // Form state for rate entry creation
  const [entryForm, setEntryForm] = useState({
    prefix: '',
    destination: '',
    countryCode: '',
    ratePerMinute: 0
  });

  // Import form
  const [importFile, setImportFile] = useState(null);

  useEffect(() => {
    fetchRateCards();
  }, []);

  const fetchRateCards = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/sip/ratecards');
      if (response.data.success) {
        // Handle both response formats: response.data.data or response.data.rateCards
        const cards = response.data.data || response.data.rateCards || [];
        setRateCards(Array.isArray(cards) ? cards : []);
      } else {
        setRateCards([]);
      }
    } catch (error) {
      logger.error('Error fetching rate cards:', error);
      showNotification?.('Failed to load rate cards', 'error');
      setRateCards([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntries = async (cardId) => {
    try {
      setLoadingEntries(true);
      const response = await axiosInstance.get(`/api/sip/ratecards/${cardId}/entries`);
      if (response.data.success) {
        setEntries(response.data.data || []);
      }
    } catch (error) {
      logger.error('Error fetching entries:', error);
      showNotification?.('Failed to load rate entries', 'error');
    } finally {
      setLoadingEntries(false);
    }
  };

  const handleCreateCard = async () => {
    try {
      setSaving(true);
      const response = await axiosInstance.post('/api/sip/ratecards', cardForm);
      if (response.data.success) {
        showNotification?.('Rate card created successfully', 'success');
        setShowCreateModal(false);
        resetCardForm();
        fetchRateCards();
      }
    } catch (error) {
      logger.error('Error creating rate card:', error);
      const errorMessage = error.response?.data?.error || 'Failed to create rate card';
      showNotification?.(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCard = async () => {
    try {
      setSaving(true);
      const response = await axiosInstance.put(`/api/sip/ratecards/${selectedCard._id}`, cardForm);
      if (response.data.success) {
        showNotification?.('Rate card updated successfully', 'success');
        setShowCreateModal(false);
        setSelectedCard(null);
        resetCardForm();
        fetchRateCards();
      }
    } catch (error) {
      logger.error('Error updating rate card:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update rate card';
      showNotification?.(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm('Are you sure you want to delete this rate card? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await axiosInstance.delete(`/api/sip/ratecards/${cardId}`);
      if (response.data.success) {
        showNotification?.('Rate card deleted successfully', 'success');
        fetchRateCards();
      }
    } catch (error) {
      logger.error('Error deleting rate card:', error);
      showNotification?.('Failed to delete rate card', 'error');
    }
  };

  const handleAddEntry = async () => {
    try {
      setSaving(true);
      const response = await axiosInstance.post(`/api/sip/ratecards/${selectedCard._id}/entries`, entryForm);
      if (response.data.success) {
        showNotification?.('Rate entry added successfully', 'success');
        resetEntryForm();
        fetchEntries(selectedCard._id);
      }
    } catch (error) {
      logger.error('Error adding entry:', error);
      const errorMessage = error.response?.data?.error || 'Failed to add rate entry';
      showNotification?.(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      const response = await axiosInstance.delete(`/api/sip/ratecards/entries/${entryId}`);
      if (response.data.success) {
        showNotification?.('Rate entry deleted', 'success');
        fetchEntries(selectedCard._id);
      }
    } catch (error) {
      logger.error('Error deleting entry:', error);
      showNotification?.('Failed to delete rate entry', 'error');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      showNotification?.('Please select a file to import', 'error');
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axiosInstance.post(
        `/api/sip/ratecards/${selectedCard._id}/import`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        showNotification?.(`Imported ${response.data.data?.imported || 0} rates successfully`, 'success');
        setShowImportModal(false);
        setImportFile(null);
        fetchEntries(selectedCard._id);
      }
    } catch (error) {
      logger.error('Error importing rates:', error);
      const errorMessage = error.response?.data?.error || 'Failed to import rates';
      showNotification?.(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicateCard = async (card) => {
    try {
      const response = await axiosInstance.post('/api/sip/ratecards', {
        ...card,
        name: `${card.name} (Copy)`,
        status: 'draft'
      });
      if (response.data.success) {
        showNotification?.('Rate card duplicated successfully', 'success');
        fetchRateCards();
      }
    } catch (error) {
      logger.error('Error duplicating rate card:', error);
      showNotification?.('Failed to duplicate rate card', 'error');
    }
  };

  const resetCardForm = () => {
    setCardForm({
      name: '',
      description: '',
      cardType: 'sell',
      currency: 'USD',
      billingIncrement: 6,
      minimumDuration: 6,
      roundingMethod: 'up',
      defaultRate: 0,
      connectionFee: 0
    });
  };

  const resetEntryForm = () => {
    setEntryForm({
      prefix: '',
      destination: '',
      countryCode: '',
      ratePerMinute: 0
    });
  };

  const openEditModal = (card) => {
    setSelectedCard(card);
    setCardForm({
      name: card.name,
      description: card.description || '',
      cardType: card.cardType,
      currency: card.currency,
      billingIncrement: card.billingIncrement,
      minimumDuration: card.minimumDuration || 6,
      roundingMethod: card.roundingMethod || 'up',
      defaultRate: card.defaultRate,
      connectionFee: card.connectionFee
    });
    setShowCreateModal(true);
  };

  const openEntriesModal = (card) => {
    setSelectedCard(card);
    fetchEntries(card._id);
    setShowEntriesModal(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircleIcon },
      inactive: { color: 'bg-gray-100 text-gray-700 dark:bg-[#1a1a1a] dark:text-gray-400', icon: ExclamationCircleIcon },
      draft: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: ClockIcon }
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const getCardTypeBadge = (type) => {
    if (type === 'buy') {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
          Buy (Cost)
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
        Sell (Revenue)
      </span>
    );
  };

  return (
    <div className="h-full bg-white dark:bg-[#111111] overflow-y-auto">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/voice')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-[#1a1a1a] rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Rate Cards
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Manage pricing and billing rates for voice calls
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setSelectedCard(null);
                resetCardForm();
                setShowCreateModal(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              New Rate Card
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
          </div>
        ) : rateCards.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 dark:bg-[#1a1a1a] rounded-lg">
            <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No rate cards</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Create a rate card to define pricing for your calls
            </p>
            <button
              onClick={() => {
                setSelectedCard(null);
                resetCardForm();
                setShowCreateModal(true);
              }}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Create Rate Card
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {rateCards.map((card) => (
              <div
                key={card._id}
                className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden"
              >
                {/* Card Header */}
                <div
                  className="p-5 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1a1a1a]/50 transition-colors"
                  onClick={() => setExpandedCard(expandedCard === card._id ? null : card._id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                        <CurrencyDollarIcon className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {card.name}
                          </h3>
                          {getCardTypeBadge(card.cardType)}
                          {getStatusBadge(card.status)}
                        </div>
                        {card.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {card.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {expandedCard === card._id ? (
                        <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Stats Row */}
                  <div className="grid grid-cols-4 gap-4 mt-4">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Currency</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {card.currency}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Default Rate</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        ${card.defaultRate?.toFixed(4)}/min
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Connection Fee</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        ${card.connectionFee?.toFixed(4)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Billing Increment</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {card.billingIncrement}s
                      </p>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedCard === card._id && (
                  <div className="border-t border-gray-200 dark:border-gray-800 p-5 bg-gray-50 dark:bg-[#1a1a1a]/50">
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEntriesModal(card);
                        }}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                      >
                        <GlobeAltIcon className="w-4 h-4" />
                        Manage Rates
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(card);
                        }}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-[#222222] transition-colors"
                      >
                        <PencilIcon className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicateCard(card);
                        }}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-[#222222] transition-colors"
                      >
                        <DocumentDuplicateIcon className="w-4 h-4" />
                        Duplicate
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCard(card._id);
                        }}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <TrashIcon className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Rate Card Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              {selectedCard ? 'Edit Rate Card' : 'Create Rate Card'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={cardForm.name}
                  onChange={(e) => setCardForm({ ...cardForm, name: e.target.value })}
                  placeholder="US Domestic Rates"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={cardForm.description}
                  onChange={(e) => setCardForm({ ...cardForm, description: e.target.value })}
                  placeholder="Optional description..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Card Type
                  </label>
                  <select
                    value={cardForm.cardType}
                    onChange={(e) => setCardForm({ ...cardForm, cardType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                  >
                    <option value="sell">Sell (Revenue)</option>
                    <option value="buy">Buy (Cost)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Currency
                  </label>
                  <select
                    value={cardForm.currency}
                    onChange={(e) => setCardForm({ ...cardForm, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CAD">CAD</option>
                    <option value="AUD">AUD</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Default Rate ($/min)
                  </label>
                  <input
                    type="number"
                    value={cardForm.defaultRate}
                    onChange={(e) => setCardForm({ ...cardForm, defaultRate: parseFloat(e.target.value) })}
                    step="0.0001"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Connection Fee ($)
                  </label>
                  <input
                    type="number"
                    value={cardForm.connectionFee}
                    onChange={(e) => setCardForm({ ...cardForm, connectionFee: parseFloat(e.target.value) })}
                    step="0.0001"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Billing Increment (sec)
                  </label>
                  <select
                    value={cardForm.billingIncrement}
                    onChange={(e) => setCardForm({ ...cardForm, billingIncrement: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                  >
                    <option value={1}>1 second</option>
                    <option value={6}>6 seconds</option>
                    <option value={30}>30 seconds</option>
                    <option value={60}>60 seconds</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Rounding Method
                  </label>
                  <select
                    value={cardForm.roundingMethod}
                    onChange={(e) => setCardForm({ ...cardForm, roundingMethod: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                  >
                    <option value="up">Round Up</option>
                    <option value="down">Round Down</option>
                    <option value="nearest">Round Nearest</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setSelectedCard(null);
                  resetCardForm();
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={selectedCard ? handleUpdateCard : handleCreateCard}
                disabled={saving || !cardForm.name}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : selectedCard ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rate Entries Modal */}
      {showEntriesModal && selectedCard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Rate Entries - {selectedCard.name}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Manage destination rates for this rate card
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setSelectedCard(selectedCard);
                    setShowImportModal(true);
                  }}
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#222222] transition-colors"
                >
                  <ArrowUpTrayIcon className="w-4 h-4" />
                  Import CSV
                </button>
              </div>
            </div>

            {/* Add Entry Form */}
            <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4 mb-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Add New Rate Entry
              </h3>
              <div className="grid grid-cols-5 gap-3">
                <input
                  type="text"
                  value={entryForm.prefix}
                  onChange={(e) => setEntryForm({ ...entryForm, prefix: e.target.value })}
                  placeholder="Prefix (e.g., 1)"
                  className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
                <input
                  type="text"
                  value={entryForm.destination}
                  onChange={(e) => setEntryForm({ ...entryForm, destination: e.target.value })}
                  placeholder="Destination (e.g., US)"
                  className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
                <input
                  type="text"
                  value={entryForm.countryCode}
                  onChange={(e) => setEntryForm({ ...entryForm, countryCode: e.target.value })}
                  placeholder="Country Code (e.g., US)"
                  className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
                <input
                  type="number"
                  value={entryForm.ratePerMinute}
                  onChange={(e) => setEntryForm({ ...entryForm, ratePerMinute: parseFloat(e.target.value) })}
                  placeholder="Rate/min"
                  step="0.0001"
                  className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
                <button
                  onClick={handleAddEntry}
                  disabled={saving || !entryForm.prefix || !entryForm.destination}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-colors disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Entries Table */}
            <div className="flex-1 overflow-y-auto">
              {loadingEntries ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 dark:border-gray-100"></div>
                </div>
              ) : entries.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <GlobeAltIcon className="mx-auto h-10 w-10 mb-2" />
                  <p>No rate entries yet. Add entries above or import a CSV file.</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                  <thead className="bg-gray-50 dark:bg-[#222222] sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Prefix
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Destination
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Country
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Rate/Min
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-[#1a1a1a] divide-y divide-gray-200 dark:divide-gray-800">
                    {entries.map((entry) => (
                      <tr key={entry._id} className="hover:bg-gray-50 dark:hover:bg-[#222222]/50">
                        <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">
                          {entry.prefix}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {entry.destination}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {entry.countryCode}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          ${entry.ratePerMinute?.toFixed(4)}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleDeleteEntry(entry._id)}
                            className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-800">
              <button
                onClick={() => {
                  setShowEntriesModal(false);
                  setSelectedCard(null);
                  setEntries([]);
                }}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Import Rate Entries
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Upload a CSV file with columns: prefix, destination, countryCode, ratePerMinute
            </p>

            <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="hidden"
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                className="cursor-pointer"
              >
                <ArrowUpTrayIcon className="mx-auto h-10 w-10 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {importFile ? importFile.name : 'Click to select CSV file'}
                </p>
              </label>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={saving || !importFile}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RateCards;
